import {
  BRUSH_PRESETS,
  DEFAULT_STROKE_OPTIONS,
  NOTEBOOK_PAGE_SIZE,
  STROKE_COLORS,
  clamp,
  createNotebookPage,
  DEFAULT_NOTEBOOK_SIZE,
  drawWorldPoints,
  eraseStrokeAt,
  nanoid,
  notebookPaper,
  strokeOutline,
  vDist,
  type BoardNode,
  type DrawPoint,
  type NotebookImage,
  type NotebookNode,
  type NotebookPaper,
  type NotebookStroke,
  type Vec,
} from '@kanvas/board-core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { readBoardClipboard, type ClipboardPayload } from '../actions/clipboard';
import type { BoardDoc } from '../collab/BoardDoc';
import { renderNodesToPng } from '../export/exportBoard';
import { useDocVersion } from '../collab/hooks';
import { api } from '../lib/api';
import { useEditor } from '../state/store';
import { ColorGrid, Popover, Segmented, Stepper, Swatch } from '../ui/controls';
import { Icon } from '../ui/icons';

type NotebookTool = 'write' | 'draw' | 'eraser' | 'hand';
type FlipDir = 'next' | 'prev';
type FlipLeaf = {
  dir: FlipDir;
  url: string;
  text: string;
  paperPad: number;
  cell: number;
};
type Cam = { x: number; y: number; zoom: number };
type ResizeCorner = 'nw' | 'ne' | 'sw' | 'se';
type ImageEdit = {
  id: string;
  mode: 'move' | ResizeCorner;
  startX: number;
  startY: number;
  orig: NotebookImage;
};

/** Book-flip duration — keep in sync with the leaf animation in styles.css. */
const FLIP_MS = 620;
/** Open / close fade of the whole workspace. */
const CLOSE_MS = 170;
const SAMPLE_SPACING = 1.6;
const NB_MIN_ZOOM = 0.35;
const NB_MAX_ZOOM = 4;
/** The page opens at least this large when the window is wide enough. */
const NB_OPEN_ZOOM = 1.6;
const FIT_PAD = 24;
/** How much of the page must stay on screen while panning. */
const PAN_KEEP = 96;
const MIN_IMAGE = 24;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function pageImages(page: NotebookNode['pages'][number] | undefined): NotebookImage[] {
  return page?.images ?? [];
}

function fitImageSize(srcW: number, srcH: number, maxW: number, maxH: number) {
  const scale = Math.min(maxW / Math.max(srcW, 1), maxH / Math.max(srcH, 1), 1);
  return { w: Math.max(MIN_IMAGE, srcW * scale), h: Math.max(MIN_IMAGE, srcH * scale) };
}

function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement;
}

function currentPage(node: NotebookNode) {
  const idx = Math.min(Math.max(node.pageIndex, 0), node.pages.length - 1);
  return { idx, page: node.pages[idx]! };
}

function patchPages(node: NotebookNode, idx: number, page: NotebookNode['pages'][number]) {
  const pages = node.pages.slice();
  pages[idx] = page;
  return pages;
}

function pageCell(pageW: number) {
  return Math.max(14, Math.min(22, pageW / 14));
}

/** Image files from a clipboard / drop payload, deduplicated by content type. */
function imageFilesFrom(data: DataTransfer | null): File[] {
  if (!data) return [];
  const files: File[] = [];
  for (const item of data.items ?? []) {
    if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
    const file = item.getAsFile();
    if (file) files.push(file);
  }
  if (files.length === 0) {
    for (const file of data.files ?? []) {
      if (file.type.startsWith('image/')) files.push(file);
    }
  }
  return files;
}

/** `<img src>` from copied HTML — what some browsers put on the clipboard for "Copy image". */
function imageUrlFromHtml(data: DataTransfer | null): string | null {
  const html = data?.getData('text/html');
  if (!html) return null;
  const match = /<img[^>]+src=["']([^"']+)["']/i.exec(html);
  const src = match?.[1];
  if (!src) return null;
  return /^(https?:|data:image\/)/i.test(src) ? src.replace(/&amp;/g, '&') : null;
}

function loadImageSize(src: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => reject(new Error('image failed to load'));
    img.src = src;
  });
}

/** Resize `orig` by dragging `corner` by (dx, dy) page units, anchored at the opposite corner. */
function resizeImage(
  orig: NotebookImage,
  corner: ResizeCorner,
  dx: number,
  dy: number,
  free: boolean,
  maxW: number,
  maxH: number,
): NotebookImage {
  const west = corner === 'nw' || corner === 'sw';
  const north = corner === 'nw' || corner === 'ne';
  let w = orig.w + (west ? -dx : dx);
  let h = orig.h + (north ? -dy : dy);
  if (free) {
    w = clamp(w, MIN_IMAGE, maxW);
    h = clamp(h, MIN_IMAGE, maxH);
  } else {
    const minScale = MIN_IMAGE / Math.min(orig.w, orig.h);
    const maxScale = Math.min(maxW / orig.w, maxH / orig.h);
    const s = clamp(Math.max(w / orig.w, h / orig.h), minScale, Math.max(minScale, maxScale));
    w = orig.w * s;
    h = orig.h * s;
  }
  return {
    ...orig,
    w,
    h,
    x: west ? orig.x + orig.w - w : orig.x,
    y: north ? orig.y + orig.h - h : orig.y,
  };
}

/**
 * Full-screen notebook workspace.
 *
 * Double-clicking a notebook on the board opens this view: the page fills the
 * window, and "На доску" (or Esc) returns to the canvas.
 */
export function NotebookWorkspace({
  doc,
  canEdit = true,
}: {
  doc: BoardDoc;
  canEdit?: boolean;
}) {
  const notebookId = useEditor((s) => s.enteredNotebookId);
  const drawStyle = useEditor((s) => s.drawStyle);
  const version = useDocVersion(doc);
  const [tool, setTool] = useState<NotebookTool>(canEdit ? 'write' : 'hand');
  const [flip, setFlip] = useState<FlipLeaf | null>(null);
  const [cam, setCamState] = useState<Cam>({ x: 0, y: 0, zoom: 1 });
  const [camAnimating, setCamAnimating] = useState(false);
  /** Canvas backing-store scale; follows zoom once it settles so ink stays crisp. */
  const [inkScale, setInkScale] = useState(1);
  const [closing, setClosing] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  /** Live geometry of the image being moved / resized; committed on release. */
  const [imagePreview, setImagePreview] = useState<NotebookImage | null>(null);
  /** Viewers flip pages locally — `pageIndex` in the doc is shared state. */
  const [viewIndex, setViewIndex] = useState<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const drawingRef = useRef<DrawPoint[] | null>(null);
  const flippingRef = useRef(false);
  const spaceRef = useRef(false);
  const panDragRef = useRef<{ x: number; y: number } | null>(null);
  const imageEditRef = useRef<ImageEdit | null>(null);
  const imageCacheRef = useRef(new Map<string, HTMLImageElement>());
  const userMovedCamRef = useRef(false);
  const camAnimTimerRef = useRef(0);
  const camRef = useRef(cam);
  camRef.current = cam;

  const pageSize = NOTEBOOK_PAGE_SIZE;

  const node = useMemo(() => {
    if (!notebookId) return null;
    const n = doc.get(notebookId);
    if (n?.type !== 'notebook') return null;
    if (canEdit || viewIndex === null) return n;
    return { ...n, pageIndex: clamp(viewIndex, 0, n.pages.length - 1) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, notebookId, version, canEdit, viewIndex]);

  /*
   * The page text the textarea shows, kept here and updated in the same tick
   * as the keystroke. The document only notifies React on the next animation
   * frame, so a textarea bound straight to it was briefly given its old
   * value back after every keystroke — and the browser then moved the caret
   * to the end. Typing at the end looked fine; going back to write anywhere
   * else lost the caret after one character.
   */
  const docPage = node ? currentPage(node).page : null;
  const docPageId = docPage?.id ?? '';
  const docText = docPage?.text ?? '';
  const [textDraft, setTextDraft] = useState({ pageId: docPageId, text: docText });
  // Someone else edited the page (or the page changed): take their text.
  useEffect(() => {
    setTextDraft((d) =>
      d.pageId === docPageId && d.text === docText ? d : { pageId: docPageId, text: docText },
    );
  }, [docPageId, docText]);
  const pageText = textDraft.pageId === docPageId ? textDraft.text : docText;

  const close = useCallback(() => {
    if (prefersReducedMotion()) {
      useEditor.getState().setEnteredNotebook(null);
      return;
    }
    setClosing(true);
  }, []);

  useEffect(() => {
    if (!closing) return;
    const id = window.setTimeout(() => useEditor.getState().setEnteredNotebook(null), CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [closing]);

  // Keep cover size fixed even for notebooks created before the lock.
  useEffect(() => {
    if (!node || !canEdit) return;
    if (node.w === DEFAULT_NOTEBOOK_SIZE.w && node.h === DEFAULT_NOTEBOOK_SIZE.h) return;
    doc.update<NotebookNode>(node.id, {
      w: DEFAULT_NOTEBOOK_SIZE.w,
      h: DEFAULT_NOTEBOOK_SIZE.h,
    });
  }, [canEdit, doc, node]);

  useEffect(() => {
    setViewIndex(null);
  }, [notebookId]);

  useEffect(() => {
    if (node && tool === 'write' && !flip) textareaRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id, node?.pageIndex, tool, flip]);

  // Ink tools draw over images, so a lingering selection box would only get in the way.
  useEffect(() => {
    if (tool === 'draw' || tool === 'eraser') setSelectedImageId(null);
  }, [tool]);

  /* ------------------------------------------------------------ camera */

  const clampCam = useCallback(
    (c: Cam): Cam => {
      const stage = stageRef.current;
      if (!stage) return c;
      const sw = stage.clientWidth;
      const sh = stage.clientHeight;
      const z = c.zoom;
      const keepX = Math.min(PAN_KEEP, (pageSize.w * z) / 2);
      const keepY = Math.min(PAN_KEEP, (pageSize.h * z) / 2);
      return {
        zoom: z,
        x: clamp(c.x, (keepX - sw) / z, pageSize.w - keepX / z),
        y: clamp(c.y, (keepY - sh) / z, pageSize.h - keepY / z),
      };
    },
    [pageSize.h, pageSize.w],
  );

  /** Move the camera; `animate` eases the jump (buttons, fit), gestures stay 1:1. */
  const setCam = useCallback(
    (next: Cam | ((c: Cam) => Cam), opts: { animate?: boolean; user?: boolean } = {}) => {
      const resolved = typeof next === 'function' ? next(camRef.current) : next;
      const clamped = clampCam(resolved);
      camRef.current = clamped;
      if (opts.user !== false) userMovedCamRef.current = true;
      window.clearTimeout(camAnimTimerRef.current);
      if (opts.animate && !prefersReducedMotion()) {
        setCamAnimating(true);
        camAnimTimerRef.current = window.setTimeout(() => setCamAnimating(false), 320);
      } else {
        setCamAnimating(false);
      }
      setCamState(clamped);
    },
    [clampCam],
  );

  useEffect(() => () => window.clearTimeout(camAnimTimerRef.current), []);

  const fitCamera = useCallback(
    (animate = false) => {
      const stage = stageRef.current;
      if (!stage) return;
      const sw = stage.clientWidth;
      const sh = stage.clientHeight;
      if (sw < 2 || sh < 2) return;
      // Fill the width; only fall back to fitting the height when the page
      // would otherwise open smaller than NB_OPEN_ZOOM. A tall page scrolls.
      const fitW = (sw - FIT_PAD * 2) / pageSize.w;
      const fitH = (sh - FIT_PAD * 2) / pageSize.h;
      const zoom = clamp(Math.min(fitW, Math.max(fitH, NB_OPEN_ZOOM)), NB_MIN_ZOOM, NB_MAX_ZOOM);
      const tall = pageSize.h * zoom + FIT_PAD * 2 > sh;
      setCam(
        {
          zoom,
          x: pageSize.w / 2 - sw / (2 * zoom),
          y: tall ? -FIT_PAD / zoom : pageSize.h / 2 - sh / (2 * zoom),
        },
        { animate, user: false },
      );
      userMovedCamRef.current = false;
    },
    [pageSize.h, pageSize.w, setCam],
  );

  const zoomBy = useCallback(
    (factor: number) => {
      const stage = stageRef.current;
      if (!stage) return;
      const c = camRef.current;
      const nextZoom = clamp(c.zoom * factor, NB_MIN_ZOOM, NB_MAX_ZOOM);
      const cx = stage.clientWidth / 2;
      const cy = stage.clientHeight / 2;
      setCam(
        {
          zoom: nextZoom,
          x: cx / c.zoom + c.x - cx / nextZoom,
          y: cy / c.zoom + c.y - cy / nextZoom,
        },
        { animate: true },
      );
    },
    [setCam],
  );

  useEffect(() => {
    const id = requestAnimationFrame(() => fitCamera());
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitCamera, node?.id]);

  // Window resize: refit until the user takes over the camera, then just keep it in bounds.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (userMovedCamRef.current) setCam((c) => c, { user: true });
      else fitCamera();
    });
    ro.observe(stage);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitCamera, setCam, node?.id]);

  useEffect(() => {
    setSelectedImageId(null);
    setImagePreview(null);
    imageEditRef.current = null;
  }, [node?.id, node?.pageIndex]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const c = camRef.current;
      const scale = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? stage.clientHeight : 1;

      if (e.ctrlKey || e.metaKey) {
        // Clamp the delta: a mouse wheel notch (~100px) would otherwise jump
        // 2.7x at once, while trackpad pinches send small deltas anyway.
        const delta = clamp(e.deltaY * scale, -40, 40);
        const nextZoom = clamp(c.zoom * Math.exp(-delta * 0.01), NB_MIN_ZOOM, NB_MAX_ZOOM);
        setCam({
          zoom: nextZoom,
          x: sx / c.zoom + c.x - sx / nextZoom,
          y: sy / c.zoom + c.y - sy / nextZoom,
        });
        return;
      }

      const dx = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX;
      const dy = e.shiftKey && !e.deltaX ? 0 : e.deltaY;
      setCam({
        ...c,
        x: c.x + (dx * scale) / c.zoom,
        y: c.y + (dy * scale) / c.zoom,
      });
    };

    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id, setCam]);

  const screenToPage = useCallback((clientX: number, clientY: number): Vec | null => {
    const stage = stageRef.current;
    if (!stage) return null;
    const rect = stage.getBoundingClientRect();
    const c = camRef.current;
    return {
      x: (clientX - rect.left) / c.zoom + c.x,
      y: (clientY - rect.top) / c.zoom + c.y,
    };
  }, []);

  /* ------------------------------------------------------------ painting */

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !node) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = pageSize.w;
    const cssH = pageSize.h;
    const res = Math.max(dpr, inkScale);
    const pxW = Math.max(1, Math.round(cssW * res));
    const pxH = Math.max(1, Math.round(cssH * res));
    if (canvas.width !== pxW || canvas.height !== pxH) {
      canvas.width = pxW;
      canvas.height = pxH;
    }
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(res, 0, 0, res, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    // Paper fills the page surface 1:1 — camera zoom/pan is CSS on the scene.
    ctx.fillStyle = '#FFFEF8';
    ctx.fillRect(0, 0, pageSize.w, pageSize.h);
    ctx.strokeStyle = 'rgba(40,50,70,0.12)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, pageSize.w - 1, pageSize.h - 1);

    const paper = notebookPaper(node);
    const cell = pageCell(pageSize.w);
    if (paper !== 'blank') {
      ctx.strokeStyle = 'rgba(90, 140, 200, 0.28)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      if (paper === 'lined') {
        for (let y = cell; y < pageSize.h - 4; y += cell) {
          ctx.moveTo(0, y);
          ctx.lineTo(pageSize.w, y);
        }
        ctx.stroke();
        ctx.strokeStyle = 'rgba(220, 80, 80, 0.35)';
        ctx.beginPath();
        ctx.moveTo(28, 0);
        ctx.lineTo(28, pageSize.h);
        ctx.stroke();
      } else {
        for (let y = cell; y < pageSize.h - 2; y += cell) {
          ctx.moveTo(0, y);
          ctx.lineTo(pageSize.w, y);
        }
        for (let x = cell; x < pageSize.w - 2; x += cell) {
          ctx.moveTo(x, 0);
          ctx.lineTo(x, pageSize.h);
        }
        ctx.stroke();
      }
    }

    const { page } = currentPage(node);
    for (const stored of pageImages(page)) {
      const image = imagePreview?.id === stored.id ? imagePreview : stored;
      let img = imageCacheRef.current.get(image.src);
      if (!img) {
        img = new Image();
        img.decoding = 'async';
        // Repaint through the ref so a late load never draws a stale page.
        img.onload = () => paintRef.current();
        img.src = image.src;
        imageCacheRef.current.set(image.src, img);
      }
      if (img.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, image.x, image.y, image.w, image.h);
      } else {
        ctx.fillStyle = 'rgba(40,50,70,0.06)';
        ctx.fillRect(image.x, image.y, image.w, image.h);
      }
    }

    const drawOutline = (points: DrawPoint[], color: string, size: number, brush: NotebookStroke['brush']) => {
      const preset = BRUSH_PRESETS[brush] ?? {};
      const outline = strokeOutline(points, { ...DEFAULT_STROKE_OPTIONS, ...preset, size });
      if (outline.length < 3) return;
      ctx.fillStyle = color;
      if (brush === 'highlighter') {
        ctx.globalAlpha = 0.45;
        ctx.globalCompositeOperation = 'multiply';
      }
      ctx.beginPath();
      ctx.moveTo(outline[0]!.x, outline[0]!.y);
      for (let i = 1; i < outline.length; i++) ctx.lineTo(outline[i]!.x, outline[i]!.y);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    };

    for (const stroke of page.strokes) {
      if (stroke.points.length < 2) continue;
      drawOutline(stroke.points, stroke.color, stroke.size, stroke.brush);
    }

    const live = drawingRef.current;
    if (live && live.length >= 2) {
      const style = useEditor.getState().drawStyle;
      drawOutline(live, style.color, style.size, style.brush);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, pageSize.h, pageSize.w, imagePreview, version, inkScale]);

  // Re-rasterise at the on-screen resolution after zooming stops, not on
  // every wheel tick — reallocating the canvas mid-gesture stutters.
  useEffect(() => {
    const dpr = window.devicePixelRatio || 1;
    const target = Math.min(4, Math.ceil(dpr * Math.max(1, cam.zoom) * 2) / 2);
    if (target === inkScale) return;
    const id = window.setTimeout(() => setInkScale(target), 160);
    return () => window.clearTimeout(id);
  }, [cam.zoom, inkScale]);

  const paintRef = useRef(paint);
  paintRef.current = paint;

  useEffect(() => {
    paint();
  }, [paint]);

  /* ------------------------------------------------------------ pages */

  const snapshotLeaf = useCallback(
    (dir: FlipDir): FlipLeaf => {
      const { page: outgoing } = currentPage(node!);
      const paper = notebookPaper(node!);
      // Sync ink to canvas, then freeze the leaf as an image so the turn
      // keeps the old page visible while the live surface swaps underneath.
      paint();
      let url = '';
      try {
        url = canvasRef.current?.toDataURL('image/png') ?? '';
      } catch {
        url = '';
      }
      return {
        dir,
        url,
        text: outgoing?.text ?? '',
        paperPad: paper === 'lined' ? 32 : 12,
        cell: pageCell(pageSize.w),
      };
    },
    [node, pageSize.w, paint],
  );

  const turn = useCallback(
    (delta: number) => {
      if (!node || flippingRef.current) return;
      const next = Math.min(Math.max(node.pageIndex + delta, 0), node.pages.length - 1);
      if (next === node.pageIndex) return;

      const commit = () => {
        if (!canEdit) {
          setViewIndex(next);
          return;
        }
        doc.update<NotebookNode>(node.id, { pageIndex: next });
        doc.breakUndoBatch();
      };

      if (prefersReducedMotion()) {
        commit();
        return;
      }

      flippingRef.current = true;
      setFlip(snapshotLeaf(delta > 0 ? 'next' : 'prev'));
      commit();
    },
    [canEdit, doc, node, snapshotLeaf],
  );

  const addPage = () => {
    if (!node || !canEdit || flippingRef.current) return;
    const pages = [...node.pages, createNotebookPage()];
    const nextIndex = pages.length - 1;
    if (!prefersReducedMotion()) {
      flippingRef.current = true;
      setFlip(snapshotLeaf('next'));
    }
    doc.update<NotebookNode>(node.id, { pages, pageIndex: nextIndex });
    doc.breakUndoBatch();
  };

  const endFlip = useCallback(() => {
    setFlip(null);
    flippingRef.current = false;
  }, []);

  // Safety net in case `animationend` never fires (tab hidden mid-turn).
  useEffect(() => {
    if (!flip) return;
    const id = window.setTimeout(endFlip, FLIP_MS + 80);
    return () => window.clearTimeout(id);
  }, [flip, endFlip]);

  const setPaper = (paper: NotebookPaper) => {
    if (!node || !canEdit) return;
    doc.update<NotebookNode>(node.id, { paper });
    doc.breakUndoBatch();
  };

  const setText = (text: string) => {
    if (!node || !canEdit) return;
    const { idx, page } = currentPage(node);
    setTextDraft({ pageId: page.id, text });
    doc.update<NotebookNode>(node.id, {
      pages: patchPages(node, idx, { ...page, text }),
    });
  };

  /* ------------------------------------------------------------ text caret */

  /**
   * Page-space position of character `index` in the page text.
   *
   * The textarea can't report where its caret is, so a hidden twin with the
   * same class, padding and wrapping lays out the text up to `index` and a
   * marker span reports its offset. Wrapped lines are measured correctly,
   * which counting "\n" would not do.
   */
  const caretPoint = useCallback((index: number): Vec | null => {
    const ta = textareaRef.current;
    const mirror = mirrorRef.current;
    if (!ta || !mirror) return null;
    mirror.textContent = ta.value.slice(0, index);
    const marker = document.createElement('span');
    marker.textContent = '\u2060'; // word joiner: no break opportunity
    mirror.appendChild(marker);
    // The rest of the text too: it decides where the word at the caret wraps.
    mirror.appendChild(document.createTextNode(ta.value.slice(index)));
    const point = { x: marker.offsetLeft, y: marker.offsetTop };
    mirror.textContent = '';
    return point;
  }, []);

  /** Pan so the caret stays on screen while typing past the view's edge. */
  const keepCaretVisible = useCallback(() => {
    const ta = textareaRef.current;
    const stage = stageRef.current;
    if (!ta || !stage || document.activeElement !== ta) return;
    const p = caretPoint(ta.selectionEnd);
    if (!p) return;
    const c = camRef.current;
    const line = pageCell(pageSize.w);
    const margin = 48;
    const viewW = stage.clientWidth / c.zoom;
    const viewH = stage.clientHeight / c.zoom;
    const m = margin / c.zoom;
    let { x, y } = c;
    if (p.y + line > y + viewH - m) y = p.y + line - viewH + m;
    else if (p.y < y + m) y = p.y - m;
    if (p.x > x + viewW - m) x = p.x - viewW + m;
    else if (p.x < x + m) x = p.x - m;
    if (x !== c.x || y !== c.y) setCam({ ...c, x, y }, { animate: true });
  }, [caretPoint, pageSize.w, setCam]);

  /**
   * Clicking empty paper below or beside the text puts the caret there.
   *
   * The page is one textarea, so a click past the end of the text used to
   * land the caret back at the end of the last line — after moving away,
   * you couldn't carry on writing where you pointed. Padding with line
   * breaks and spaces up to the click makes any spot on the page writable.
   */
  const placeCaretAtClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    if (!node || !canEdit || tool !== 'write' || flip || e.button !== 0) return;
    if (ta.selectionStart !== ta.selectionEnd) return; // a text selection, leave it
    const p = screenToPage(e.clientX, e.clientY);
    if (!p) return;

    const style = getComputedStyle(ta);
    const line = parseFloat(style.lineHeight) || pageCell(pageSize.w);
    const padTop = parseFloat(style.paddingTop) || 0;
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const measure = document.createElement('canvas').getContext('2d');
    if (!measure) return;
    measure.font = style.font;
    const spaceW = Math.max(measure.measureText(' ').width, 1);

    const text = ta.value;
    const end = caretPoint(text.length);
    const caret = ta.selectionEnd;
    const at = caretPoint(caret);
    if (!end || !at) return;

    const rowOf = (y: number) => Math.floor((y - padTop) / line);
    const clickRow = rowOf(p.y);
    let insert = '';
    let insertAt = caret;

    if (clickRow > rowOf(end.y)) {
      // Below the last line: new lines down to the click, then indent.
      insertAt = text.length;
      insert = '\n'.repeat(clickRow - rowOf(end.y)) + ' '.repeat(Math.max(0, Math.round((p.x - padLeft) / spaceW)));
    } else if (rowOf(at.y) === clickRow && p.x > at.x + spaceW * 2) {
      // Past the end of a line: indent up to the click.
      insert = ' '.repeat(Math.round((p.x - at.x) / spaceW));
    }
    if (!insert) return;

    const next = text.slice(0, insertAt) + insert + text.slice(insertAt);
    const caretAt = insertAt + insert.length;
    setText(next);
    doc.breakUndoBatch();
    // The controlled value re-renders from the doc; put the caret after it.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      el.setSelectionRange(caretAt, caretAt);
    });
  };

  /* ------------------------------------------------------------ images */

  const hitPageImage = useCallback(
    (p: Vec): NotebookImage | null => {
      if (!node) return null;
      const images = pageImages(currentPage(node).page);
      for (let i = images.length - 1; i >= 0; i--) {
        const image = images[i]!;
        if (p.x >= image.x && p.x <= image.x + image.w && p.y >= image.y && p.y <= image.y + image.h) {
          return image;
        }
      }
      return null;
    },
    [node],
  );

  // Latest node for async inserts: uploads resolve after further edits.
  const nodeRef = useRef(node);
  nodeRef.current = node;

  const placeImageOnPage = useCallback(
    (src: string, srcW: number, srcH: number, at?: Vec) => {
      const current = nodeRef.current;
      if (!current || !canEdit) return;
      const { idx, page } = currentPage(current);
      const size = fitImageSize(srcW, srcH, pageSize.w * 0.6, pageSize.h * 0.5);
      let cx: number;
      let cy: number;
      if (at) {
        cx = at.x;
        cy = at.y;
      } else {
        const stage = stageRef.current;
        const c = camRef.current;
        cx = c.x + (stage?.clientWidth ?? pageSize.w) / (2 * c.zoom);
        cy = c.y + (stage?.clientHeight ?? pageSize.h) / (2 * c.zoom);
        // Cascade repeated pastes so they don't stack exactly on top of each other.
        const existing = pageImages(page).length;
        cx += (existing % 5) * 24;
        cy += (existing % 5) * 24;
      }
      const image: NotebookImage = {
        id: `ni_${nanoid(10)}`,
        src,
        x: clamp(cx - size.w / 2, 8, Math.max(8, pageSize.w - size.w - 8)),
        y: clamp(cy - size.h / 2, 8, Math.max(8, pageSize.h - size.h - 8)),
        w: size.w,
        h: size.h,
      };
      doc.update<NotebookNode>(current.id, {
        pages: patchPages(current, idx, {
          ...page,
          images: [...pageImages(page), image],
        }),
      });
      doc.breakUndoBatch();
      if (tool === 'draw' || tool === 'eraser') setTool('write');
      textareaRef.current?.blur();
      setSelectedImageId(image.id);
    },
    [canEdit, doc, pageSize.h, pageSize.w, tool],
  );

  const addImageFile = useCallback(
    async (file: File, at?: Vec) => {
      if (!canEdit || !file.type.startsWith('image/')) return;
      try {
        const asset = await api.uploadAsset(file);
        placeImageOnPage(asset.url, asset.width, asset.height, at);
      } catch {
        // Upload and local fallback both failed — leave the page unchanged.
        useEditor
          .getState()
          .notify('Картинку не удалось загрузить. Возможно, файл больше 25 МБ или формат не поддерживается');
      }
    },
    [canEdit, placeImageOnPage],
  );

  const addImageUrl = useCallback(
    async (src: string) => {
      if (!canEdit) return;
      try {
        const size = await loadImageSize(src);
        placeImageOnPage(src, size.w, size.h);
      } catch {
        // Not a loadable image — ignore.
      }
    },
    [canEdit, placeImageOnPage],
  );

  /** Centre of what is on screen, in page coordinates. */
  const viewCentre = (): Vec => {
    const stage = stageRef.current;
    const c = camRef.current;
    return {
      x: c.x + (stage?.clientWidth ?? pageSize.w) / (2 * c.zoom),
      y: c.y + (stage?.clientHeight ?? pageSize.h) / (2 * c.zoom),
    };
  };

  /**
   * Objects copied on the board, pasted into the notebook.
   *
   * The board's clipboard format used to land in the page text as a marker
   * line of JSON (or nothing at all). Each kind now arrives as the closest
   * thing a page has: photos as page images, pen strokes as page ink the
   * eraser can work on, plain text blocks as text at the caret while typing,
   * and anything else — stickies, shapes, connectors, a mixed selection — as
   * a picture that looks exactly like it did on the board.
   */
  const pasteBoardNodes = useCallback(
    async (payload: ClipboardPayload, typing: boolean) => {
      const current = nodeRef.current;
      if (!current || !canEdit) return;
      const nodes = payload.nodes.filter(
        (n) => n.type !== 'group' && n.type !== 'comment' && !n.hidden,
      );
      if (nodes.length === 0) return;

      // Text blocks into the text you are typing.
      if (typing && nodes.every((n) => n.type === 'text')) {
        const ta = textareaRef.current;
        const insert = nodes.map((n) => (n as { text: string }).text).join('\n');
        if (ta) {
          const start = ta.selectionStart;
          const next = ta.value.slice(0, start) + insert + ta.value.slice(ta.selectionEnd);
          setText(next);
          doc.breakUndoBatch();
          const caret = start + insert.length;
          requestAnimationFrame(() => textareaRef.current?.setSelectionRange(caret, caret));
          return;
        }
      }

      // Plain photos keep their source; nothing to re-encode.
      if (
        nodes.every(
          (n) => n.type === 'image' && !n.crop && !(n.mask && n.mask.length >= 3) && !n.rotation,
        )
      ) {
        for (const n of nodes) {
          const image = n as Extract<BoardNode, { type: 'image' }>;
          placeImageOnPage(image.src, image.w, image.h);
        }
        return;
      }

      // Pen strokes become page strokes, laid out as they were, scaled to fit.
      if (nodes.every((n) => n.type === 'draw' && !n.closed)) {
        const strokes = nodes.map((n) => {
          const draw = n as Extract<BoardNode, { type: 'draw' }>;
          return { draw, world: drawWorldPoints(draw) };
        });
        const all = strokes.flatMap((s) => s.world);
        if (all.length === 0) return;
        const minX = Math.min(...all.map((p) => p.x));
        const minY = Math.min(...all.map((p) => p.y));
        const w = Math.max(Math.max(...all.map((p) => p.x)) - minX, 1);
        const h = Math.max(Math.max(...all.map((p) => p.y)) - minY, 1);
        const scale = Math.min(1, (pageSize.w * 0.72) / w, (pageSize.h * 0.6) / h);
        const centre = viewCentre();
        const left = clamp(centre.x - (w * scale) / 2, 8, Math.max(8, pageSize.w - w * scale - 8));
        const top = clamp(centre.y - (h * scale) / 2, 8, Math.max(8, pageSize.h - h * scale - 8));
        const added: NotebookStroke[] = strokes.map(({ draw, world }) => ({
          id: `ns_${nanoid(10)}`,
          points: world.map(
            (p, i) =>
              [(p.x - minX) * scale + left, (p.y - minY) * scale + top, draw.points[i]?.[2] ?? 0.5] as DrawPoint,
          ),
          color: draw.color,
          size: Math.max(1, draw.size * scale),
          brush: draw.brush,
        }));
        const { idx, page } = currentPage(current);
        doc.update<NotebookNode>(current.id, {
          pages: patchPages(current, idx, { ...page, strokes: [...page.strokes, ...added] }),
        });
        doc.breakUndoBatch();
        return;
      }

      // Everything else: a picture of the selection as it looks on the board.
      try {
        const blob = await renderNodesToPng(nodes, payload.bounds);
        if (!blob) throw new Error('render failed');
        const asset = await api.uploadAsset(new File([blob], 'board.png', { type: 'image/png' }));
        // Board size, not the 2x raster's, so it lands at its real scale.
        placeImageOnPage(asset.url, payload.bounds.w, payload.bounds.h);
      } catch {
        useEditor.getState().notify('Не удалось вставить объекты с доски. Попробуйте ещё раз');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, doc, pageSize.h, pageSize.w, placeImageOnPage],
  );

  const removeSelectedImage = useCallback(() => {
    if (!node || !canEdit || !selectedImageId) return;
    const { idx, page } = currentPage(node);
    const next = pageImages(page).filter((image) => image.id !== selectedImageId);
    doc.update<NotebookNode>(node.id, {
      pages: patchPages(node, idx, { ...page, images: next }),
    });
    doc.breakUndoBatch();
    setSelectedImageId(null);
  }, [canEdit, doc, node, selectedImageId]);

  const selectImage = (id: string | null) => {
    setSelectedImageId(id);
    // Leave the textarea so Delete / Backspace reach the image, not the text.
    if (id) textareaRef.current?.blur();
  };

  const beginImageEdit = (e: React.PointerEvent, image: NotebookImage, mode: ImageEdit['mode']) => {
    e.preventDefault();
    e.stopPropagation();
    selectImage(image.id);
    imageEditRef.current = { id: image.id, mode, startX: e.clientX, startY: e.clientY, orig: image };
    setImagePreview(image);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const moveImageEdit = (e: React.PointerEvent) => {
    const edit = imageEditRef.current;
    if (!edit) return false;
    const zoom = camRef.current.zoom || 1;
    const dx = (e.clientX - edit.startX) / zoom;
    const dy = (e.clientY - edit.startY) / zoom;
    const o = edit.orig;
    if (edit.mode === 'move') {
      setImagePreview({
        ...o,
        x: clamp(o.x + dx, 0, Math.max(0, pageSize.w - o.w)),
        y: clamp(o.y + dy, 0, Math.max(0, pageSize.h - o.h)),
      });
    } else {
      setImagePreview(resizeImage(o, edit.mode, dx, dy, e.shiftKey, pageSize.w, pageSize.h));
    }
    return true;
  };

  const endImageEdit = (e: React.PointerEvent) => {
    const edit = imageEditRef.current;
    if (!edit) return false;
    imageEditRef.current = null;
    const el = e.currentTarget as HTMLElement;
    if (el.hasPointerCapture?.(e.pointerId)) el.releasePointerCapture(e.pointerId);
    const result = imagePreview;
    setImagePreview(null);
    const current = nodeRef.current;
    if (!current || !canEdit || !result || result.id !== edit.id) return true;
    const o = edit.orig;
    if (result.x === o.x && result.y === o.y && result.w === o.w && result.h === o.h) return true;
    const { idx, page } = currentPage(current);
    doc.update<NotebookNode>(current.id, {
      pages: patchPages(current, idx, {
        ...page,
        images: pageImages(page).map((image) => (image.id === edit.id ? { ...image, ...result } : image)),
      }),
    });
    doc.breakUndoBatch();
    return true;
  };

  /* ------------------------------------------------------------ keyboard & clipboard */

  useEffect(() => {
    if (!node) return;

    const releaseSpace = () => {
      spaceRef.current = false;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') releaseSpace();
    };

    const onKey = (e: KeyboardEvent) => {
      const primary = e.ctrlKey || e.metaKey;
      if (e.key === 'Escape') {
        e.preventDefault();
        releaseSpace();
        if (selectedImageId) setSelectedImageId(null);
        else close();
        return;
      }
      if (primary && (e.code === 'Equal' || e.code === 'NumpadAdd')) {
        e.preventDefault();
        zoomBy(1.25);
        return;
      }
      if (primary && (e.code === 'Minus' || e.code === 'NumpadSubtract')) {
        e.preventDefault();
        zoomBy(0.8);
        return;
      }
      if (primary && (e.code === 'Digit0' || e.code === 'Numpad0')) {
        e.preventDefault();
        fitCamera(true);
        return;
      }
      if (isTypingTarget(e.target)) return;
      if (e.code === 'Space') {
        spaceRef.current = true;
        e.preventDefault();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedImageId) {
        e.preventDefault();
        removeSelectedImage();
        return;
      }
      if (primary || e.altKey) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        turn(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        turn(1);
      } else if (e.code === 'KeyH') {
        e.preventDefault();
        setTool('hand');
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') releaseSpace();
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', releaseSpace);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', releaseSpace);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [close, fitCamera, node, removeSelectedImage, selectedImageId, turn, zoomBy]);

  useEffect(() => {
    if (!node || !canEdit) return;
    const onPaste = (e: ClipboardEvent) => {
      // A copy from the board comes first, typing or not: its plain-text
      // fallback is a marker line of JSON that must never reach the page.
      const board = readBoardClipboard(e.clipboardData);
      if (board) {
        e.preventDefault();
        void pasteBoardNodes(board, isTypingTarget(e.target));
        return;
      }
      // Images win even while typing: pasting a screenshot into the page
      // text should drop the picture on the page, not do nothing.
      const files = imageFilesFrom(e.clipboardData);
      if (files.length > 0) {
        e.preventDefault();
        for (const file of files) void addImageFile(file);
        return;
      }
      if (isTypingTarget(e.target)) return;
      const url = imageUrlFromHtml(e.clipboardData);
      if (url) {
        e.preventDefault();
        void addImageUrl(url);
        return;
      }
      // Plain text pasted outside the textarea goes to the end of the page text.
      const text = e.clipboardData?.getData('text/plain');
      if (text) {
        e.preventDefault();
        const { page } = currentPage(node);
        const base = page.text ?? '';
        setText(base && !base.endsWith('\n') ? `${base}\n${text}` : `${base}${text}`);
        doc.breakUndoBatch();
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addImageFile, addImageUrl, canEdit, node, pasteBoardNodes]);

  /* ------------------------------------------------------------ pointer */

  const wantsPan = (e: { button: number }) =>
    tool === 'hand' || spaceRef.current || e.button === 1 || e.button === 2;

  const onPanDown = (e: React.PointerEvent) => {
    if (flip || !wantsPan(e)) return false;
    e.preventDefault();
    e.stopPropagation();
    panDragRef.current = { x: e.clientX, y: e.clientY };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    return true;
  };

  const onPanMove = (e: React.PointerEvent) => {
    const drag = panDragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    panDragRef.current = { x: e.clientX, y: e.clientY };
    setCam((c) => ({ ...c, x: c.x - dx / c.zoom, y: c.y - dy / c.zoom }));
  };

  const onPanUp = (e: React.PointerEvent) => {
    if (!panDragRef.current) return;
    panDragRef.current = null;
    const el = e.currentTarget as HTMLElement;
    if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!node || flip) return;
    const p = screenToPage(e.clientX, e.clientY);
    if (!p) return;

    // Images can be grabbed with the text and hand tools (hand still pans on empty paper).
    if (canEdit && e.button === 0 && !spaceRef.current && (tool === 'hand' || tool === 'write')) {
      const hit = hitPageImage(p);
      if (hit) {
        beginImageEdit(e, hit, 'move');
        return;
      }
      setSelectedImageId(null);
    }

    if (onPanDown(e)) return;
    if (!canEdit || tool === 'write' || tool === 'hand' || e.button !== 0) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

    if (tool === 'draw') {
      drawingRef.current = [[p.x, p.y, e.pressure > 0 ? e.pressure : 0.5]];
      paint();
      return;
    }

    eraseAt(p);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (panDragRef.current) {
      onPanMove(e);
      return;
    }
    if (moveImageEdit(e)) return;

    if (!node || !canEdit || flip || tool === 'write' || tool === 'hand') return;
    if (!(e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) return;
    const p = screenToPage(e.clientX, e.clientY);
    if (!p) return;

    if (tool === 'draw' && drawingRef.current) {
      const pts = drawingRef.current;
      // Coalesced events keep fast strokes smooth instead of polygonal.
      const samples = e.nativeEvent.getCoalescedEvents?.() ?? [];
      const events = samples.length > 0 ? samples : [e.nativeEvent];
      const zoom = camRef.current.zoom || 1;
      let added = false;
      for (const ev of events) {
        const q = screenToPage(ev.clientX, ev.clientY);
        if (!q) continue;
        const last = pts[pts.length - 1]!;
        if (vDist({ x: last[0], y: last[1] }, q) < SAMPLE_SPACING / zoom) continue;
        pts.push([q.x, q.y, ev.pressure > 0 ? ev.pressure : 0.5]);
        added = true;
      }
      if (added) paint();
      return;
    }

    if (tool === 'eraser') eraseAt(p);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (panDragRef.current) {
      onPanUp(e);
      return;
    }
    if (endImageEdit(e)) return;
    if (!node) return;
    const el = e.currentTarget as HTMLElement;
    if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    if (drawingRef.current) {
      const pts = drawingRef.current;
      drawingRef.current = null;
      if (pts.length >= 2 && canEdit) {
        const { idx, page } = currentPage(node);
        const stroke: NotebookStroke = {
          id: `ns_${nanoid(10)}`,
          points: pts,
          color: drawStyle.color,
          size: drawStyle.size,
          brush: drawStyle.brush,
        };
        doc.update<NotebookNode>(node.id, {
          pages: patchPages(node, idx, { ...page, strokes: [...page.strokes, stroke] }),
        });
        doc.breakUndoBatch();
      } else {
        paint();
      }
      return;
    }
    if (tool === 'eraser') doc.breakUndoBatch();
  };

  const eraseAt = (at: Vec) => {
    if (!node || !canEdit) return;
    const { idx, page } = currentPage(node);
    const radius = Math.max(drawStyle.size, 10);
    let changed = false;
    const nextStrokes: NotebookStroke[] = [];
    for (const stroke of page.strokes) {
      const remnants = eraseStrokeAt(stroke.points, at, radius + stroke.size / 2);
      if (remnants.length === 1 && remnants[0]!.length === stroke.points.length) {
        nextStrokes.push(stroke);
        continue;
      }
      changed = true;
      for (const pts of remnants) {
        if (pts.length < 2) continue;
        nextStrokes.push({ ...stroke, id: `ns_${nanoid(10)}`, points: pts });
      }
    }
    if (changed) {
      doc.update<NotebookNode>(node.id, {
        pages: patchPages(node, idx, { ...page, strokes: nextStrokes }),
      });
    }
  };

  if (!node) return null;

  const { page } = currentPage(node);
  const paper = notebookPaper(node);
  const cell = pageCell(pageSize.w);
  const paperPad = paper === 'lined' ? 32 : 12;
  const storedSelected = selectedImageId
    ? pageImages(page).find((image) => image.id === selectedImageId) ?? null
    : null;
  const selected =
    storedSelected && imagePreview?.id === storedSelected.id ? imagePreview : storedSelected;

  return (
    <div
      className={`notebook-workspace${closing ? ' is-closing' : ''}`}
      role="dialog"
      aria-label={node.title || 'Тетрадь'}
    >
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          for (const file of files) void addImageFile(file);
        }}
      />
      <header className="notebook-workspace-bar">
        <button type="button" className="notebook-back" onClick={close}>
          <Icon name="arrowLeft" size={18} />
          На доску
        </button>

        <div className="notebook-workspace-title">{node.title || 'Тетрадь'}</div>

        <div className="notebook-workspace-tools" role="toolbar" aria-label="Инструменты тетради">
          <button
            type="button"
            className={`icon-btn ${tool === 'hand' ? 'is-active' : ''}`}
            aria-label="Перемещение"
            aria-pressed={tool === 'hand'}
            data-tip="Перемещение (H)"
            onClick={() => setTool('hand')}
          >
            <Icon name="hand" size={18} />
          </button>
          {canEdit && (
            <>
              <button
                type="button"
                className={`icon-btn ${tool === 'write' ? 'is-active' : ''}`}
                aria-label="Писать"
                aria-pressed={tool === 'write'}
                data-tip="Текст"
                onClick={() => setTool('write')}
              >
                <Icon name="text" size={18} />
              </button>
              <button
                type="button"
                className={`icon-btn ${tool === 'eraser' ? 'is-active' : ''}`}
                aria-label="Ластик"
                aria-pressed={tool === 'eraser'}
                data-tip="Ластик"
                onClick={() => setTool('eraser')}
              >
                <Icon name="eraser" size={18} />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Добавить картинку"
                data-tip="Картинка (или Ctrl+V)"
                onClick={() => imageInputRef.current?.click()}
              >
                <Icon name="image" size={18} />
              </button>
              {selectedImageId && (
                <button
                  type="button"
                  className="icon-btn notebook-pop-in"
                  aria-label="Удалить картинку"
                  data-tip="Удалить картинку (Del)"
                  onClick={removeSelectedImage}
                >
                  <Icon name="trash" size={18} />
                </button>
              )}

              <div
                className={`notebook-ink-style ${tool === 'draw' ? 'is-drawing' : ''}`}
                role="group"
                aria-label="Стиль линии"
              >
                <Segmented
                  value={drawStyle.brush}
                  options={[
                    { value: 'pen', label: 'Перо', icon: 'pen' },
                    { value: 'marker', label: 'Маркер', icon: 'marker' },
                    { value: 'highlighter', label: 'Выделитель', icon: 'highlighter' },
                  ]}
                  onChange={(brush) => {
                    useEditor.getState().patchDrawStyle({ brush });
                    setTool('draw');
                  }}
                />
                <Popover
                  title="Цвет"
                  trigger={() => (
                    <>
                      <Swatch color={drawStyle.color} />
                      <Icon name="chevronDown" size={12} />
                    </>
                  )}
                >
                  {(closeMenu) => (
                    <ColorGrid
                      colors={STROKE_COLORS}
                      value={drawStyle.color}
                      onPick={(color) => {
                        useEditor.getState().patchDrawStyle({ color });
                        setTool('draw');
                        closeMenu();
                      }}
                    />
                  )}
                </Popover>
                <Stepper
                  value={drawStyle.size}
                  min={1}
                  max={64}
                  suffix="px"
                  onChange={(size) => {
                    useEditor.getState().patchDrawStyle({ size });
                    if (tool !== 'eraser' && tool !== 'hand') setTool('draw');
                  }}
                />
              </div>
            </>
          )}
        </div>

        <div className="notebook-workspace-zoom" role="group" aria-label="Масштаб">
          <button
            type="button"
            className="icon-btn"
            aria-label="Уменьшить"
            data-tip="Уменьшить (Ctrl −)"
            disabled={cam.zoom <= NB_MIN_ZOOM + 1e-3}
            onClick={() => zoomBy(0.8)}
          >
            <Icon name="minus" size={16} />
          </button>
          <button
            type="button"
            className="notebook-zoom-value"
            data-tip="По размеру окна (Ctrl 0)"
            onClick={() => fitCamera(true)}
          >
            {Math.round(cam.zoom * 100)}%
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label="Увеличить"
            data-tip="Увеличить (Ctrl +)"
            disabled={cam.zoom >= NB_MAX_ZOOM - 1e-3}
            onClick={() => zoomBy(1.25)}
          >
            <Icon name="plus" size={16} />
          </button>
        </div>

        <div className="notebook-workspace-pager">
          <button
            type="button"
            className="icon-btn"
            aria-label="Предыдущая страница"
            disabled={!!flip || node.pageIndex <= 0}
            onClick={() => turn(-1)}
          >
            <Icon name="chevronLeft" size={18} />
          </button>
          <span aria-live="polite">
            {node.pageIndex + 1} / {node.pages.length}
          </span>
          <button
            type="button"
            className="icon-btn"
            aria-label="Следующая страница"
            disabled={!!flip || node.pageIndex >= node.pages.length - 1}
            onClick={() => turn(1)}
          >
            <Icon name="chevronRight" size={18} />
          </button>
          {canEdit && (
            <button
              type="button"
              className="icon-btn"
              aria-label="Новая страница"
              data-tip="Новая страница"
              disabled={!!flip}
              onClick={addPage}
            >
              <Icon name="plus" size={18} />
            </button>
          )}
        </div>

        {canEdit && (
          <div className="notebook-paper-switch" role="group" aria-label="Тип бумаги">
            {(
              [
                ['grid', 'Клетка'],
                ['lined', 'Линейка'],
                ['blank', 'Чистая'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={paper === value ? 'is-active' : ''}
                aria-pressed={paper === value}
                onClick={() => setPaper(value)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </header>

      <div
        ref={stageRef}
        className={`notebook-workspace-stage${tool === 'hand' ? ' is-hand' : ''}`}
        onPointerDown={(e) => {
          if (e.button === 0 && tool !== 'hand' && !spaceRef.current) setSelectedImageId(null);
          onPanDown(e);
        }}
        onPointerMove={onPanMove}
        onPointerUp={onPanUp}
        onPointerCancel={onPanUp}
        onContextMenu={(e) => e.preventDefault()}
        onDragOver={(e) => {
          if (!canEdit) return;
          if ([...e.dataTransfer.types].includes('Files')) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }
        }}
        onDrop={(e) => {
          if (!canEdit) return;
          e.preventDefault();
          const at = screenToPage(e.clientX, e.clientY) ?? undefined;
          imageFilesFrom(e.dataTransfer).forEach((file, i) => {
            void addImageFile(file, at ? { x: at.x + i * 24, y: at.y + i * 24 } : undefined);
          });
        }}
      >
        <div
          className={`notebook-flip-scene${flip ? ` is-flipping is-flipping-${flip.dir}` : ''}${
            camAnimating ? ' is-cam-animating' : ''
          }`}
          style={
            {
              width: pageSize.w,
              height: pageSize.h,
              transform: `translate(${-cam.x * cam.zoom}px, ${-cam.y * cam.zoom}px) scale(${cam.zoom})`,
              '--nb-inv-zoom': 1 / cam.zoom,
            } as React.CSSProperties
          }
        >
          <div
            className={`notebook-page-surface is-${tool}${flip ? ' is-under-flip' : ''}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <canvas ref={canvasRef} className="notebook-page-canvas" />
            <textarea
              ref={textareaRef}
              className="notebook-page-text"
              value={pageText}
              spellCheck
              readOnly={!canEdit || tool !== 'write' || !!flip}
              style={{
                paddingLeft: paperPad,
                lineHeight: `${cell}px`,
                pointerEvents: canEdit && tool === 'write' && !flip ? 'auto' : 'none',
              }}
              onChange={(e) => {
                setText(e.target.value);
                requestAnimationFrame(keepCaretVisible);
              }}
              onClick={placeCaretAtClick}
              onKeyUp={(e) => {
                if (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End' || e.key.startsWith('Page')) {
                  keepCaretVisible();
                }
              }}
              // Text taller than the page would scroll the textarea's own
              // content and slide it off the ruled lines; pin it instead.
              onScroll={(e) => {
                e.currentTarget.scrollTop = 0;
                e.currentTarget.scrollLeft = 0;
              }}
              onBlur={() => doc.breakUndoBatch()}
              aria-label={`Страница ${node.pageIndex + 1}`}
            />
            <div
              ref={mirrorRef}
              className="notebook-page-text notebook-text-mirror"
              style={{ paddingLeft: paperPad, lineHeight: `${cell}px` }}
              aria-hidden
            />
          </div>

          {canEdit && selected && !flip && (
            <div
              key={selected.id}
              className={`notebook-image-selection${imagePreview ? ' is-editing' : ''}`}
              style={{ left: selected.x, top: selected.y, width: selected.w, height: selected.h }}
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                beginImageEdit(e, storedSelected!, 'move');
              }}
              onPointerMove={moveImageEdit}
              onPointerUp={endImageEdit}
              onPointerCancel={endImageEdit}
            >
              {(['nw', 'ne', 'sw', 'se'] as const).map((corner) => (
                <span
                  key={corner}
                  className={`notebook-image-handle is-${corner}`}
                  onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    beginImageEdit(e, storedSelected!, corner);
                  }}
                  onPointerMove={moveImageEdit}
                  onPointerUp={endImageEdit}
                  onPointerCancel={endImageEdit}
                />
              ))}
              {imagePreview && imageEditRef.current?.mode !== 'move' && (
                <span className="notebook-image-size">
                  {Math.round(selected.w)} × {Math.round(selected.h)}
                </span>
              )}
            </div>
          )}

          {flip && (
            <div
              className={`notebook-page-leaf is-${flip.dir}`}
              aria-hidden
              onAnimationEnd={(e) => {
                if (e.target === e.currentTarget) endFlip();
              }}
            >
              <div className="notebook-page-leaf-face is-front">
                {flip.url ? (
                  <img className="notebook-page-leaf-shot" src={flip.url} alt="" draggable={false} />
                ) : (
                  <div className="notebook-page-leaf-shot notebook-page-leaf-fallback" />
                )}
                {flip.text ? (
                  <div
                    className="notebook-page-text notebook-page-leaf-text"
                    style={{ paddingLeft: flip.paperPad, lineHeight: `${flip.cell}px` }}
                  >
                    {flip.text}
                  </div>
                ) : null}
                <div className="notebook-page-leaf-shade" />
              </div>
              <div className="notebook-page-leaf-face is-back" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
